import type { AuthenticationError } from './AuthContext';

export type TrackError = (
  label: string,
  error?: Error,
  data?: {
    [k: string]: unknown;
  },
) => void;

/**
 * Scrubbed failure for a session read rejected by upstream rate limiting.
 * auth-fe reports only `error.name` per failure, so this literal name is the
 * Sentry/telemetry tag that keeps rate-limited reads separable in reports.
 * It must stay a literal: bundlers mangle class names, so `<class>.name`
 * cannot be trusted. Detection is by `instanceof` in
 * `authenticationRequestError`; this constant only feeds reporting.
 */
export const SESSION_RATE_LIMITED_ERROR_NAME = 'SessionRateLimitedError';

export class SessionRateLimitedError extends Error {
  constructor() {
    super('Session request failed');
    this.name = SESSION_RATE_LIMITED_ERROR_NAME;
  }
}

export function authenticationRequestError(
  error: unknown,
  trackError: TrackError | undefined,
): AuthenticationError {
  const message = 'Session request failed';
  // Transport errors can embed session credentials in their message, cause, and payload.
  trackError?.('Session error', new Error(message), {
    errorName: error instanceof Error ? error.name : 'Error',
  });
  return {
    state: 'error',
    user: undefined,
    message,
    rateLimited: error instanceof SessionRateLimitedError,
  };
}
